/**
 * Purge Table Component
 *
 * Table displaying purge data with:
 * - Period (Month or Day based on aggregation level)
 * - Data Purged count
 * - Generate Report action button
 */

import React, { useMemo } from 'react';
import { Download } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Table } from 'equal-ds-ui';
import type { TableColumn } from 'equal-ds-ui';
import { formatNumberWithCommas, isPeriodCompleted, getMonthNameFromPeriod } from '../../../utils/purge-utils';
import type { PurgeTableRow, PurgeAggregationLevel } from '../../../types/purge';

export interface PurgeTableProps {
  data: PurgeTableRow[];
  aggregationLevel: PurgeAggregationLevel;
  onGenerateReport: (row: PurgeTableRow) => void;
  isLoading?: boolean;
  isGeneratingReport?: boolean;
}

export const PurgeTable: React.FC<PurgeTableProps> = ({
  data,
  aggregationLevel,
  onGenerateReport,
  isLoading = false,
  isGeneratingReport = false,
}) => {
  // Helper function to check if a row represents the current period
  const isCurrentPeriod = (period: string, level: PurgeAggregationLevel): boolean => {
    const now = new Date();

    if (level === 'day') {
      const today = now.toISOString().split('T')[0]; // YYYY-MM-DD
      return period === today;
    } else {
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`; // YYYY-MM
      return period === currentMonth;
    }
  };

  // Define columns - conditionally include Generate Report based on feature flag
  const columns = useMemo<TableColumn<PurgeTableRow>[]>(() => {
    const baseColumns: TableColumn<PurgeTableRow>[] = [
      {
        key: 'displayPeriod',
        header: aggregationLevel === 'month' ? 'Month' : 'Date',
        width: 250,
        render: (_value, row) => {
          const isCurrent = isCurrentPeriod(row.period, aggregationLevel);
          const tooltipText = aggregationLevel === 'month'
            ? 'Data for the current month is preliminary. Final counts will be available once the month ends, as purge operations are still ongoing.'
            : 'Data for today is preliminary. Final counts will be available at the end of the day, as purge operations continue throughout the day.';

          return (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-primary">
                {row.displayPeriod}
              </span>
              {isCurrent && (
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded cursor-help hover:bg-amber-200 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                        aria-label="Data accuracy information"
                      >
                        {aggregationLevel === 'month' ? 'Current Month' : 'Current Day'}
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        className="max-w-xs px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg z-50"
                        sideOffset={5}
                      >
                        {tooltipText}
                        <Tooltip.Arrow className="fill-gray-900" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              )}
            </div>
          );
        },
      },
      {
        key: 'total_count',
        header: 'Data Purged',
        width: 200,
        render: (_value, row) => (
          <span className="text-sm font-semibold text-text-primary">
            {formatNumberWithCommas(row.total_count)}
          </span>
        ),
      },
    ];

    // Only add Purge Report column in monthly view
    if (aggregationLevel === 'month') {
      baseColumns.push({
        key: 'actions',
        header: 'Purge Report',
        width: 200,
        locked: true,
        render: (_value, row) => {
          const isCompleted = isPeriodCompleted(row.period);
          const monthName = getMonthNameFromPeriod(row.period);

          // Current/future month: disabled button with tooltip
          if (!isCompleted) {
            return (
              <Tooltip.Provider delayDuration={200}>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <button
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-text-primary bg-background-subtle border border-border-subtle rounded-md opacity-50 cursor-not-allowed"
                      disabled
                      aria-label={`Report not available - ${monthName} is not yet complete`}
                    >
                      <Download size={16} />
                      Download Report
                    </button>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="max-w-xs px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg z-50"
                      sideOffset={5}
                    >
                      Available after {monthName} ends
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            );
          }

          // Completed months: enabled button
          return (
            <button
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-text-primary bg-background-subtle border border-border-subtle rounded-md hover:bg-background-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => onGenerateReport(row)}
              disabled={isGeneratingReport}
            >
              <Download size={16} />
              Download Report
            </button>
          );
        },
      });
    }

    return baseColumns;
  }, [aggregationLevel, onGenerateReport, isGeneratingReport]);

  // Handle empty state
  if (!isLoading && data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-border-subtle p-8">
        <div className="text-center">
          <p className="text-text-secondary text-sm">
            No purge data available for the selected date range.
          </p>
          <p className="text-text-tertiary text-xs mt-2">
            Try adjusting your filters or selecting a different date range.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-border-subtle">
      <div className="equal-ds-root">
        <Table
          className="shadow-sm rounded-xl"
          columns={columns}
          data={data}
          loading={isLoading}
        />
      </div>
    </div>
  );
};
